'use client';

import {useCallback} from 'react';
import {useCiamAuthContext} from './CiamAuthProvider';
import {useAuth0} from '@auth0/auth0-react';
import {buildRedirectUrlTo, StepsPath} from '../routing/StepsRouting';

export const useEmailAuth = () => {
  const {
    webAuth,
    codeRequestingStatus,
    setCodeRequestingStatus,
    codeSendingStatus,
    setCodeSendingStatus,
  } = useCiamAuthContext();

  const requestCode = useCallback(
    (email: string) => {
      setCodeRequestingStatus('pending');
      webAuth?.passwordlessStart(
        {
          connection: 'email',
          send: 'code',
          email,
        },
        err => {
          if (err) {
            console.log('Code request error', err);
            setCodeRequestingStatus('error');
            return;
          }

          setCodeRequestingStatus('success');
        },
      );
    },
    [webAuth, setCodeRequestingStatus],
  );

  const applyCode = useCallback(
    (email: string, code: string) => {
      setCodeSendingStatus('pending');

      webAuth?.passwordlessLogin(
        {
          connection: 'email',
          verificationCode: code,
          email,
        },
        err => {
          if (err) {
            console.log('Code request error', err);
            setCodeSendingStatus('error');
            return;
          }

          setCodeSendingStatus('success');
        },
      );
    },
    [webAuth, setCodeSendingStatus],
  );
  const logout = useCallback(() => {
    webAuth?.logout({
      // after logout should allways redirect to sign in page
      returnTo: buildRedirectUrlTo(StepsPath.SignIn),
    });
  }, [webAuth]);

  return {
    codeRequestingStatus,
    requestCode,
    codeSendingStatus,
    applyCode,
    logout,
  };
};

/**
 * By default uses google login
 * if provided email will send OTP
 */
export const useAuth0Login = () => {
  const {loginWithRedirect} = useAuth0();
  const handleGoogleSignIn = useCallback(
    () =>
      loginWithRedirect({
        appState: {
          returnTo: window.location.href,
        },
        authorizationParams: {
          redirect_uri: window.location.href,
          connection: 'google-oauth2',
          screen_hint: 'signup',
        },
      }),
    [loginWithRedirect, window.location.href],
  );

  const auth = useEmailAuth();
  const login = useCallback(
    (email?: string) => {
      if (!email) {
        return handleGoogleSignIn();
      }

      return auth.requestCode(email);
    },
    [handleGoogleSignIn, auth.requestCode],
  );

  return {
    ...auth,
    login,
  };
};
